import React, { useEffect, useState, useCallback } from 'react';
import { useApi } from '../../hooks/useApi';
import { useClub } from '../../hooks/useClub';

// ─── KPI pill ────────────────────────────────────────────────────────────────
function KPIPill({ label, value, color, small }) {
  return (
    <div style={{
      background: 'var(--surface-2)',
      border: '1px solid var(--border)',
      borderRadius: 8,
      padding: small ? '8px 12px' : '10px 14px',
      minWidth: small ? 90 : 110,
      flex: '1 1 0',
      textAlign: 'center',
    }}>
      <div style={{ fontSize: 10, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: 1, marginBottom: 4 }}>
        {label}
      </div>
      <div style={{ fontSize: small ? 18 : 22, fontWeight: 700, color: color || 'var(--text)', fontFamily: 'DM Mono, monospace' }}>
        {value ?? '—'}
      </div>
    </div>
  );
}

// ─── KPI group ───────────────────────────────────────────────────────────────
function KPIGroup({ title, icon, children }) {
  return (
    <div style={{ marginBottom: 24 }}>
      <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: 1.5, marginBottom: 10 }}>
        {icon} {title}
      </div>
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
        {children}
      </div>
    </div>
  );
}

// ─── 15-KPI bar (horizontal scroll, matching Google Sheet) ───────────────────
function KPIBar({ kpi }) {
  const metrics = [
    { label: 'Active Trials',      value: kpi.activeTrials },
    { label: 'Converted',          value: kpi.converted,              color: 'var(--green)' },
    { label: '% Converted',        value: `${kpi.conversionRate}%` },
    { label: '% 0 Visits',         value: `${kpi.pctZeroVisits}%`,    color: kpi.pctZeroVisits > 30 ? 'var(--red)' : 'var(--text)' },
    { label: '% 2+ Visits',        value: `${kpi.pct2PlusVisits}%` },
    { label: '% 3+ Visits',        value: `${kpi.pct3PlusVisits}%` },
    { label: '% 4+ Visits',        value: `${kpi.pct4PlusVisits}%`,   color: 'var(--green)' },
    { label: 'Avg Visits',         value: kpi.avgVisits },
    { label: '% Suggestions',      value: `${kpi.pctSuggestions}%` },
    { label: '% Confirm Booked',   value: `${kpi.pctConfirmBooked}%` },
    { label: '% Promo',            value: `${kpi.pctPromoPresented}%` },
    { label: '% Interventions',    value: `${kpi.pctInterventions}%` },
    { label: '% Mgr. Connection',  value: `${kpi.pctMgrConnection}%` },
    { label: 'Trial→Member %',     value: `${kpi.pctMembershipSold}%`, color: 'var(--yellow)' },
    { label: '% Offer Made',       value: `${kpi.pctOfferMade}%` },
    { label: 'PB101 Source %',     value: `${kpi.pctPb101Source}%` },
    { label: 'Lead Engine Source %', value: `${kpi.pctLeadSource}%` },
    { label: 'Avg Days to Conv.',  value: kpi.avgDaysToConvert || '—' },
  ];
  return (
    <div style={{
      display: 'flex', gap: 6, overflowX: 'auto', paddingBottom: 8,
      borderBottom: '1px solid var(--border)', marginBottom: 24,
    }}>
      {metrics.map(m => (
        <div key={m.label} style={{
          background: 'var(--surface-2)', border: '1px solid var(--border)',
          borderRadius: 6, padding: '6px 10px', whiteSpace: 'nowrap', flexShrink: 0,
          textAlign: 'center',
        }}>
          <div style={{ fontSize: 9, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: 0.8, marginBottom: 2 }}>
            {m.label}
          </div>
          <div style={{ fontSize: 16, fontWeight: 700, color: m.color || 'var(--text)', fontFamily: 'DM Mono, monospace' }}>
            {m.value}
          </div>
        </div>
      ))}
    </div>
  );
}

// ─── Urgent panel card ───────────────────────────────────────────────────────
function UrgentCard({ member }) {
  const dayColor = member.trialDay >= 12 ? 'var(--red)'
    : member.trialDay >= 9  ? 'var(--orange)'
    : member.trialDay >= 5  ? 'var(--yellow)'
    : 'var(--blue)';
  return (
    <div style={{
      background: 'var(--surface-2)',
      border: `1px solid ${member.atRisk ? 'var(--red)' : 'var(--border)'}`,
      borderRadius: 8, padding: '10px 12px',
      display: 'flex', justifyContent: 'space-between', alignItems: 'center',
      marginBottom: 6,
    }}>
      <div>
        <div style={{ fontWeight: 600, fontSize: 13 }}>{member.name}</div>
        {member.nextAction && (
          <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 2 }}>{member.nextAction}</div>
        )}
      </div>
      <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
        {member.atRisk && (
          <span style={{ background: 'var(--red)', color: '#fff', fontSize: 9, fontWeight: 700, padding: '2px 6px', borderRadius: 4 }}>
            AT RISK
          </span>
        )}
        <span style={{ background: dayColor, color: '#000', fontSize: 10, fontWeight: 700, padding: '2px 8px', borderRadius: 4 }}>
          DAY {member.trialDay}
        </span>
        <span style={{ fontSize: 11, color: 'var(--text-muted)', minWidth: 28, textAlign: 'right' }}>
          {member.visits}v
        </span>
      </div>
    </div>
  );
}

// ─── Upcoming card ───────────────────────────────────────────────────────────
function UpcomingCard({ member }) {
  const nextDay   = member.checkpointDay;
  const dayColor  = nextDay >= 12 ? 'var(--red)' : nextDay >= 9 ? 'var(--orange)' : nextDay >= 5 ? 'var(--yellow)' : 'var(--blue)';
  const daysAway  = nextDay - member.trialDay;
  return (
    <div style={{
      background: 'var(--surface-2)', border: '1px solid var(--border)',
      borderRadius: 8, padding: '10px 12px',
      display: 'flex', justifyContent: 'space-between', alignItems: 'center',
      marginBottom: 6,
    }}>
      <div style={{ fontWeight: 600, fontSize: 13 }}>{member.name}</div>
      <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
        <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>
          {daysAway <= 0 ? 'today' : `in ${daysAway}d`}
        </span>
        <span style={{ background: dayColor, color: '#000', fontSize: 10, fontWeight: 700, padding: '2px 8px', borderRadius: 4 }}>
          DAY {nextDay}
        </span>
      </div>
    </div>
  );
}

// ─── Main component ──────────────────────────────────────────────────────────
export default function T2MOverview() {
  const { get } = useApi();
  const { currentClub } = useClub();
  const [kpi, setKpi]       = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError]   = useState(null);

  const load = useCallback(() => {
    setLoading(true);
    setError(null);
    get(`/t2m/overview?club=${currentClub.id}`)
      .then(setKpi)
      .catch(e => setError(e.message))
      .finally(() => setLoading(false));
  }, [currentClub.id]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { load(); }, [load]);

  if (loading) return (
    <div style={{ padding: 40, textAlign: 'center' }}>
      <div className="spinner" />
    </div>
  );

  if (error) return (
    <div style={{ padding: 24, color: 'var(--red)', textAlign: 'center' }}>
      Failed to load overview — {error}
      <button onClick={load} style={{ marginLeft: 12, background: 'var(--surface-2)', border: '1px solid var(--border)', color: 'var(--text)', padding: '4px 12px', borderRadius: 6, cursor: 'pointer' }}>
        Retry
      </button>
    </div>
  );

  if (!kpi) return null;

  return (
    <div style={{ padding: '16px 0' }}>

      {/* ── 15-KPI scrolling bar ── */}
      <KPIBar kpi={kpi} />

      {/* ── Two-column layout: KPI groups left, panels right ── */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 340px', gap: 24, alignItems: 'start' }}>

        {/* LEFT — KPI groups */}
        <div>
          <KPIGroup title="Pipeline" icon="📊">
            <KPIPill label="Active Trials"    value={kpi.activeTrials} />
            <KPIPill label="Converted"        value={kpi.converted}          color="var(--green)" />
            <KPIPill label="Conv. %"          value={`${kpi.conversionRate}%`} />
            <KPIPill label="Trial→Member %"   value={`${kpi.pctMembershipSold}%`} color="var(--yellow)" />
            <KPIPill label="At Risk"          value={kpi.atRisk}             color="var(--red)" />
            <KPIPill label="Not Converted"    value={kpi.lost}               color="var(--text-muted)" />
            <KPIPill label="Pending Verify"   value={kpi.pendingVerify}      color="var(--yellow)" />
          </KPIGroup>

          <KPIGroup title="Source Breakdown" icon="📣">
            <KPIPill label="PB101 Source %"        value={`${kpi.pctPb101Source}%`}  small />
            <KPIPill label="Lead Engine Source %"  value={`${kpi.pctLeadSource}%`}   small />
            <KPIPill label="% Offer Made"          value={`${kpi.pctOfferMade}%`}    small />
          </KPIGroup>

          <KPIGroup title="Visit Checkpoints" icon="📅">
            <KPIPill label="% 0 Visits"    value={`${kpi.pctZeroVisits}%`}   color={kpi.pctZeroVisits > 30 ? 'var(--red)' : undefined} small />
            <KPIPill label="% 2+ Visits"   value={`${kpi.pct2PlusVisits}%`}  small />
            <KPIPill label="% 3+ Visits"   value={`${kpi.pct3PlusVisits}%`}  small />
            <KPIPill label="% 4+ Visits"   value={`${kpi.pct4PlusVisits}%`}  color="var(--green)" small />
            <KPIPill label="Avg Visits"    value={kpi.avgVisits}             small />
            <KPIPill label="Avg Last Day"  value={kpi.avgLastVisitDay || '—'} small />
          </KPIGroup>

          <KPIGroup title="Engagement" icon="🤝">
            <KPIPill label="% Suggestions"   value={`${kpi.pctSuggestions}%`}    small />
            <KPIPill label="% Booked D1"     value={`${kpi.pctConfirmBooked}%`}  small />
            <KPIPill label="% Promo"         value={`${kpi.pctPromoPresented}%`} small />
            <KPIPill label="% Interventions" value={`${kpi.pctInterventions}%`}  small />
            <KPIPill label="% Mgr. Conn."    value={`${kpi.pctMgrConnection}%`}  small />
            <KPIPill label="Avg Days Conv."  value={kpi.avgDaysToConvert || '—'} small />
          </KPIGroup>
        </div>

        {/* RIGHT — Action panels */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>

          {/* AT RISK & DUE TODAY panel */}
          <div style={{
            background: 'var(--surface-2)', border: '1px solid var(--border)',
            borderRadius: 10, overflow: 'hidden',
          }}>
            <div style={{
              background: 'rgba(220,38,38,0.15)', borderBottom: '1px solid var(--border)',
              padding: '10px 14px', display: 'flex', justifyContent: 'space-between', alignItems: 'center',
            }}>
              <span style={{ fontWeight: 700, fontSize: 12, textTransform: 'uppercase', letterSpacing: 1, color: 'var(--red)' }}>
                🔴 At Risk &amp; Due Today
              </span>
              <span style={{ background: 'var(--red)', color: '#fff', fontSize: 10, fontWeight: 700, padding: '2px 8px', borderRadius: 10 }}>
                {(kpi.urgentPanel || []).length}
              </span>
            </div>
            <div style={{ padding: '10px 12px', maxHeight: 320, overflowY: 'auto' }}>
              {(kpi.urgentPanel || []).length === 0 ? (
                <div style={{ color: 'var(--text-muted)', fontSize: 12, textAlign: 'center', padding: '12px 0' }}>
                  No urgent items today ✓
                </div>
              ) : (
                kpi.urgentPanel.map(m => <UrgentCard key={m.id} member={m} />)
              )}
            </div>
          </div>

          {/* UPCOMING 3 DAYS panel */}
          <div style={{
            background: 'var(--surface-2)', border: '1px solid var(--border)',
            borderRadius: 10, overflow: 'hidden',
          }}>
            <div style={{
              background: 'rgba(234,179,8,0.12)', borderBottom: '1px solid var(--border)',
              padding: '10px 14px', display: 'flex', justifyContent: 'space-between', alignItems: 'center',
            }}>
              <span style={{ fontWeight: 700, fontSize: 12, textTransform: 'uppercase', letterSpacing: 1, color: 'var(--yellow)' }}>
                📅 Upcoming — Next 3 Days
              </span>
              <span style={{ background: 'rgba(234,179,8,0.3)', color: 'var(--yellow)', fontSize: 10, fontWeight: 700, padding: '2px 8px', borderRadius: 10 }}>
                {(kpi.upcomingPanel || []).length}
              </span>
            </div>
            <div style={{ padding: '10px 12px', maxHeight: 280, overflowY: 'auto' }}>
              {(kpi.upcomingPanel || []).length === 0 ? (
                <div style={{ color: 'var(--text-muted)', fontSize: 12, textAlign: 'center', padding: '12px 0' }}>
                  No upcoming checkpoints
                </div>
              ) : (
                kpi.upcomingPanel.map(m => <UpcomingCard key={m.id} member={m} />)
              )}
            </div>
          </div>

        </div>
      </div>
    </div>
  );
}
